import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiUserCheck, FiCalendar, FiArrowRight, FiPhone, FiPlus, FiBriefcase, FiActivity, FiCheckCircle, FiDollarSign, FiCreditCard, FiTarget, FiHome, FiLayers, FiFileText, FiDownload } from 'react-icons/fi';
import { dashboardAPI, b2bAgenciesAPI, meetingsAPI, refundsAPI, activitiesAPI } from '../services/api';
import StatusBadge from '../components/Common/StatusBadge';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';
import { toast } from 'react-toastify';

/** Compare next_followup (DATE or string) to today in Asia/Kolkata (calendar day). */
function isFollowupOverdueDate(nextFollowup) {
  if (!nextFollowup) return false;
  const dueDay = String(nextFollowup).slice(0, 10);
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const todayStr = today.toISOString().slice(0, 10);
  return dueDay < todayStr;
}

function csvEscapeCell(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function indiaTodayIsoKolkata() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10);
}

/** India team report: UTF-8 CSV with BOM for Excel; raw numbers (no locale) in numeric columns. */
function downloadIndiaTeamReportCsv(rows, t, dateFilter) {
  const colDefs = [
    ['dashboard.colUser', 'name', false],
    ['dashboard.colRole', 'role', false],
    ['dashboard.colActivitiesTotal', 'activities_total', true],
    ['dashboard.colActivitiesFu', 'activities_follow_up', true],
    ['dashboard.colActivitiesMtg', 'activities_meeting', true],
    ['dashboard.colActivitiesCall', 'activities_call', true],
    ['dashboard.colMeetSch', 'meetings_scheduled', true],
    ['dashboard.colMeetResch', 'meetings_rescheduled', true],
    ['dashboard.colMeetDone', 'meetings_completed', true],
    ['dashboard.colAgrGen', 'agreements_generated_agencies', true],
    ['dashboard.colAgrUp', 'agreements_uploaded_agencies', true],
    ['dashboard.colAgrVer', 'agreements_verified', true],
    ['dashboard.colAgrUnver', 'agreements_unverified', true],
    ['dashboard.colStudents', 'student_count', true],
    ['dashboard.colAppFeeCnt', 'application_fee_records', true],
    ['dashboard.colAppFeeSum', 'application_fee_total', true],
    ['dashboard.colTuiCnt', 'tuition_fee_records', true],
    ['dashboard.colTuiSum', 'tuition_fee_total', true],
    ['dashboard.colVisa', 'visa_received_count', true],
    ['dashboard.colB2bAgencies', 'b2b_agencies_count', true],
    ['dashboard.colB2bActive', 'b2b_active_agencies_count', true]
  ];
  const headerLine = colDefs.map(([hk]) => csvEscapeCell(t(hk))).join(',');
  const bodyLines = (rows || []).map((r) =>
    colDefs
      .map(([, field, isNum]) => {
        const raw = r[field];
        const cell = isNum ? Number(raw ?? 0) : raw ?? '';
        return csvEscapeCell(cell);
      })
      .join(',')
  );
  const csv = `\uFEFF${[headerLine, ...bodyLines].join('\r\n')}`;
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const d = today.toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const range =
    dateFilter?.active && dateFilter?.date_from && dateFilter?.date_to
      ? `_${dateFilter.date_from}_to_${dateFilter.date_to}`
      : '';
  a.download = `india-workspace-reports${range}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** India B2B agency rows: UTF-8 CSV with BOM. */
function downloadIndiaB2bAgencyReportCsv(rows, t, dateFilter) {
  const colDefs = [
    ['dashboard.colB2bAgencyName', 'b2b_name', false],
    ['dashboard.colRefNumber', 'ref_number', false],
    ['dashboard.colStudents', 'student_count', true],
    ['dashboard.colAppFeeCnt', 'application_fee_records', true],
    ['dashboard.colAppFeeSum', 'application_fee_total', true],
    ['dashboard.colTuiCnt', 'tuition_fee_records', true],
    ['dashboard.colTuiSum', 'tuition_fee_total', true],
    ['dashboard.colVisa', 'visa_received_count', true]
  ];
  const headerLine = colDefs.map(([hk]) => csvEscapeCell(t(hk))).join(',');
  const bodyLines = (rows || []).map((r) =>
    colDefs
      .map(([, field, isNum]) => {
        const raw = r[field];
        const cell = isNum ? Number(raw ?? 0) : raw ?? '';
        return csvEscapeCell(cell);
      })
      .join(',')
  );
  const csv = `\uFEFF${[headerLine, ...bodyLines].join('\r\n')}`;
  const today = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const d = today.toISOString().slice(0, 10);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const range =
    dateFilter?.active && dateFilter?.date_from && dateFilter?.date_to
      ? `_${dateFilter.date_from}_to_${dateFilter.date_to}`
      : '';
  a.download = `india-b2b-agencies-report${range}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function MultiSelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  allText,
  noneText,
  selectAllText,
  unselectAllText
}) {
  const [search, setSearch] = useState('');
  const selectedSet = useMemo(() => new Set(selectedValues || []), [selectedValues]);
  const allValues = useMemo(() => (options || []).map((o) => String(o.value)), [options]);
  const filteredOptions = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => String(o.label || '').toLowerCase().includes(q));
  }, [options, search]);
  const allSelected = allValues.length > 0 && allValues.every((v) => selectedSet.has(v));
  const selectedCount = (selectedValues || []).length;
  const normalizeUiText = (value, fallback) => {
    const v = String(value || '').trim();
    if (!v || v.includes('.')) return fallback;
    return v;
  };
  const safeAllText = normalizeUiText(allText, 'All');
  const safeNoneText = normalizeUiText(noneText, 'None');
  const title = selectedCount === 0
    ? safeNoneText
    : (allSelected ? safeAllText : `${selectedCount} ${label.toLowerCase()}`);
  const selectAllLabel = normalizeUiText(selectAllText, 'Select All');
  const unselectAllLabel = normalizeUiText(unselectAllText, 'Unselect All');

  return (
    <details style={{ position: 'relative' }}>
      <summary className="btn btn-outline btn-sm" style={{ cursor: 'pointer', listStyle: 'none' }}>
        {label}: {title}
      </summary>
      <div
        style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          zIndex: 20,
          minWidth: 270,
          maxWidth: 360,
          marginTop: 8,
          background: '#ffffff',
          opacity: 1,
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
          padding: 10,
          backdropFilter: 'none'
        }}
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => onChange(allValues)}
          >
            {selectAllLabel}
          </button>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => onChange([])}
          >
            {unselectAllLabel}
          </button>
        </div>
        <input
          type="text"
          className="form-control"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${label.toLowerCase()} by name...`}
          style={{
            marginBottom: 8,
            fontSize: 13,
            height: 36,
            borderRadius: 10,
            border: '1px solid #D0D7E2',
            background: '#F8FAFC',
            color: '#0F172A',
            padding: '8px 12px',
            outline: 'none',
            boxShadow: 'none'
          }}
        />
        <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gap: 6 }}>
          {filteredOptions.map((opt) => {
            const value = String(opt.value);
            const checked = selectedSet.has(value);
            return (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange(Array.from(new Set([...(selectedValues || []), value])));
                    } else {
                      onChange((selectedValues || []).filter((v) => String(v) !== value));
                    }
                  }}
                />
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
              </label>
            );
          })}
          {filteredOptions.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No matches found</div>
          )}
        </div>
      </div>
    </details>
  );
}

const Dashboard = () => {
  const { user } = useAuth();
  const { getConfig, activeCountry } = useCountry();
  const config = getConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({});
  const [recentLeads, setRecentLeads] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [overdueMeetingsCount, setOverdueMeetingsCount] = useState(0);
  const [overdueMeetings, setOverdueMeetings] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [completingFollowupId, setCompletingFollowupId] = useState(null);
  const [conversionData, setConversionData] = useState([]);
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundStats, setRefundStats] = useState(null);
  const [indiaReports, setIndiaReports] = useState(null);
  const [indiaB2bAgencyReport, setIndiaB2bAgencyReport] = useState(null);
  const [indiaDateFrom, setIndiaDateFrom] = useState(() => indiaTodayIsoKolkata());
  const [indiaDateTo, setIndiaDateTo] = useState(() => indiaTodayIsoKolkata());
  const [indiaWorkspaceLoading, setIndiaWorkspaceLoading] = useState(false);
  const [indiaReportsResolved, setIndiaReportsResolved] = useState(false);
  const [selectedIndiaReportUsers, setSelectedIndiaReportUsers] = useState([]);
  const [selectedIndiaReportAgencies, setSelectedIndiaReportAgencies] = useState([]);
  const indiaReportReqSeq = useRef(0);
  const lastIndiaReportFetchKey = useRef('');

  const reloadIndiaWorkspaceReports = useCallback(async (range) => {
    if (String(activeCountry || '').toUpperCase() !== 'INDIA') return;
    const from = range?.from != null ? String(range.from).trim().slice(0, 10) : '';
    const to = range?.to != null ? String(range.to).trim().slice(0, 10) : '';
    const params = from && to && from <= to ? { date_from: from, date_to: to } : {};
    const fetchKey = `${String(activeCountry || '').toUpperCase()}|${from}|${to}|${String(user?.role || '')}|${String(user?.user_id || '')}`;
    if (lastIndiaReportFetchKey.current === fetchKey && indiaReportsResolved) return;
    lastIndiaReportFetchKey.current = fetchKey;
    const cacheKey = `india_workspace_reports:${fetchKey}`;
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (cached?.indiaReports || cached?.indiaB2bAgencyReport) {
          if (cached.indiaReports) setIndiaReports(cached.indiaReports);
          if (cached.indiaB2bAgencyReport) setIndiaB2bAgencyReport(cached.indiaB2bAgencyReport);
          setIndiaReportsResolved(true);
        }
      }
    } catch (_) {}
    const seq = ++indiaReportReqSeq.current;
    setIndiaWorkspaceLoading(true);
    try {
      const [ir, b2b] = await Promise.all([
        dashboardAPI.getIndiaReports(params).catch(() => ({ data: { enabled: false } })),
        dashboardAPI.getIndiaB2bAgencyReport(params).catch(() => ({ data: { enabled: false } }))
      ]);
      if (seq !== indiaReportReqSeq.current) return;
      setIndiaReports(ir.data || { enabled: false, mode: 'none', summary: null, rows: [] });
      setIndiaB2bAgencyReport(b2b.data || { enabled: false, mode: 'none', rows: [] });
      setIndiaReportsResolved(true);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({
          indiaReports: ir.data || { enabled: false, mode: 'none', summary: null, rows: [] },
          indiaB2bAgencyReport: b2b.data || { enabled: false, mode: 'none', rows: [] }
        }));
      } catch (_) {}
    } finally {
      if (seq === indiaReportReqSeq.current) setIndiaWorkspaceLoading(false);
    }
  }, [activeCountry, indiaReportsResolved, user?.role, user?.user_id]);

  useEffect(() => {
    if (String(activeCountry || '').toUpperCase() !== 'INDIA') return;
    const from = String(indiaDateFrom || '').trim().slice(0, 10);
    const to = String(indiaDateTo || '').trim().slice(0, 10);
    if (!from || !to || from > to) return;
    const timer = setTimeout(() => {
      reloadIndiaWorkspaceReports({ from, to });
    }, 250);
    return () => clearTimeout(timer);
  }, [activeCountry, indiaDateFrom, indiaDateTo, reloadIndiaWorkspaceReports]);

  const isAdmin = ['super_admin', 'admin'].includes(user?.role);
  const isAdminOrManager = ['super_admin', 'admin', 'sales_manager'].includes(user?.role);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const meetingRequired = getConfig().features.meetingRequired;
        const todayStr = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })).toISOString().slice(0, 10);
        const yesterday = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().slice(0, 10);
        const recentLeadsParams = { limit: 8, sort_by: 'created_at', sort_order: 'DESC' };
        if (!isAdmin && user?.user_id) recentLeadsParams.owner_id = user.user_id;
        const isIndiaLoad = String(activeCountry || '').toUpperCase() === 'INDIA';
        const [
          statsRes, chartsRes, followupsRes, leadsRes,
          upcomingScheduledRes, upcomingRescheduledRes,
          overdueScheduledRes, overdueRescheduledRes,
          overdueScheduledListRes, overdueRescheduledListRes,
          birthdaysRes
        ] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getCharts().catch(() => ({ data: {} })),
          activitiesAPI.getFollowups({ scope: 'today_overdue' }).catch((e) => {
            console.warn('Dashboard pending follow-ups failed:', e?.response?.data || e?.message);
            return { data: { items: [] } };
          }),
          b2bAgenciesAPI.getAll(recentLeadsParams).catch(() => ({ data: { agencies: [] } })),
          meetingRequired ? meetingsAPI.getAll({ status: 'Scheduled', date_from: todayStr, limit: 6 }).catch(() => ({ data: { meetings: [] } })) : Promise.resolve({ data: { meetings: [] } }),
          meetingRequired ? meetingsAPI.getAll({ status: 'Rescheduled', date_from: todayStr, limit: 6 }).catch(() => ({ data: { meetings: [] } })) : Promise.resolve({ data: { meetings: [] } }),
          meetingRequired ? meetingsAPI.getAll({ status: 'Scheduled', date_to: yesterdayStr, limit: 1 }).catch(() => ({ data: { total: 0 } })) : Promise.resolve({ data: { total: 0 } }),
          meetingRequired ? meetingsAPI.getAll({ status: 'Rescheduled', date_to: yesterdayStr, limit: 1 }).catch(() => ({ data: { total: 0 } })) : Promise.resolve({ data: { total: 0 } }),
          meetingRequired ? meetingsAPI.getAll({ status: 'Scheduled', date_to: yesterdayStr, limit: 6 }).catch(() => ({ data: { meetings: [] } })) : Promise.resolve({ data: { meetings: [] } }),
          meetingRequired ? meetingsAPI.getAll({ status: 'Rescheduled', date_to: yesterdayStr, limit: 6 }).catch(() => ({ data: { meetings: [] } })) : Promise.resolve({ data: { meetings: [] } }),
          dashboardAPI.getBirthdaysToday().catch(() => ({ data: [] }))
        ]);
        setStats(statsRes.data || {});
        const followupPayload = followupsRes?.data?.items;
        setFollowups(Array.isArray(followupPayload) ? followupPayload : []);
        if (chartsRes.data?.leadsByStatus) setConversionData(chartsRes.data.leadsByStatus);
        setRecentLeads(leadsRes.data?.agencies || leadsRes.data?.leads || []);
        const mergedUpcomingMeetings = [
          ...(upcomingScheduledRes.data?.meetings || []),
          ...(upcomingRescheduledRes.data?.meetings || [])
        ]
          .sort((a, b) => {
            const aDateTime = new Date(`${String(a.meeting_date).slice(0, 10)}T${a.meeting_time || '00:00:00'}`);
            const bDateTime = new Date(`${String(b.meeting_date).slice(0, 10)}T${b.meeting_time || '00:00:00'}`);
            return aDateTime - bDateTime;
          })
          .slice(0, 6);
        setUpcomingMeetings(mergedUpcomingMeetings);
        setOverdueMeetingsCount((overdueScheduledRes.data?.total || 0) + (overdueRescheduledRes.data?.total || 0));
        const mergedOverdueMeetings = [
          ...(overdueScheduledListRes.data?.meetings || []),
          ...(overdueRescheduledListRes.data?.meetings || [])
        ]
          .sort((a, b) => new Date(a.meeting_date) - new Date(b.meeting_date))
          .slice(0, 6);
        setOverdueMeetings(mergedOverdueMeetings);
        setBirthdaysToday(Array.isArray(birthdaysRes.data) ? birthdaysRes.data : []);
        if (!isIndiaLoad) {
          setIndiaDateFrom('');
          setIndiaDateTo('');
          setIndiaReports({ enabled: false, mode: 'none', summary: null, rows: [] });
          setIndiaB2bAgencyReport({ enabled: false, mode: 'none', rows: [] });
          setIndiaReportsResolved(false);
        } else {
          const todayStr = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }))
            .toISOString()
            .slice(0, 10);
          setIndiaDateFrom((f) => f || todayStr);
          setIndiaDateTo((t) => t || todayStr);
        }
      } catch (err) { toast.error(t('dashboard.failedLoad')); }
      setLoading(false);
    };
    loadDashboard();
    if (['super_admin', 'admin', 'sales_manager'].includes(user?.role)) {
      refundsAPI.getDashboardStats().then((r) => setRefundStats(r.data)).catch(() => {});
    }
  }, [activeCountry, user?.user_id, user?.role, reloadIndiaWorkspaceReports]);

  const handleCompleteFollowup = async (followupItem, e) => {
    e?.stopPropagation();
    const activityId = followupItem?.item_id;
    if (activityId == null || activityId === '') return;
    setCompletingFollowupId(activityId);
    try {
      await activitiesAPI.markFollowupDone(activityId, followupItem?.source_table || 'activity');
      toast.success(t('dashboard.followupMarkedComplete'));
      setFollowups((prev) => prev.filter((x) => !(String(x.source_table) === String(followupItem?.source_table) && String(x.item_id) === String(activityId))));
    } catch (err) {
      toast.error(err.response?.data?.message || t('dashboard.followupCompleteFailed'));
    } finally {
      setCompletingFollowupId(null);
    }
  };

  const isIndiaCRM = String(activeCountry || '').toUpperCase() === 'INDIA';
  const indiaTeamViewerRoles = ['super_admin', 'admin', 'sales_manager', 'manager'];
  const isIndiaTeamViewer = indiaTeamViewerRoles.includes(String(user?.role || '').toLowerCase());
  const holdIndiaCardsUntilReportReady = isIndiaCRM && isIndiaTeamViewer && !indiaReportsResolved;
  const showIndiaTeamTable =
    isIndiaCRM && indiaReports?.enabled && isIndiaTeamViewer;
  const showIndiaUserCards =
    isIndiaCRM && indiaReports?.enabled && !showIndiaTeamTable;
  const hideDefaultIndiaStatCards = showIndiaTeamTable || showIndiaUserCards || holdIndiaCardsUntilReportReady;
  const showIndiaB2bAgencyTable = isIndiaCRM && indiaB2bAgencyReport?.enabled;
  const indiaB2bAgencySubtitleKey =
    indiaB2bAgencyReport?.mode === 'full'
      ? 'dashboard.indiaB2bAgencyReportSubtitleFull'
      : 'dashboard.indiaB2bAgencyReportSubtitleScoped';

  const fmtIn = (n) => Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const fr = stats.franceKeyMetrics || {};
  const dateLocale = isFranceCRM ? 'fr-FR' : 'en-IN';
  const dateTimeZone = isFranceCRM ? 'Europe/Paris' : 'Asia/Kolkata';
  const computeAge = (dob) => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const today = new Date(new Date().toLocaleString('en-US', { timeZone: dateTimeZone }));
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age >= 0 ? age : null;
  };

  const franceOverviewCards = isFranceCRM
    ? [
        {
          label: t('dashboard.franceEnrolledStudents'),
          value: fr.enrolledStudents ?? 0,
          icon: <FiUserCheck />,
          colorClass: 'teal',
          link: '/students?status=Enrolled'
        },
        {
          label: t('dashboard.franceLeadsProspects'),
          value: fr.leadsAndProspects ?? 0,
          icon: <FiUsers />,
          colorClass: 'blue',
          link: '/prospects'
        },
        {
          label: t('dashboard.franceReadyToPresent'),
          value: fr.readyToPresentStudents ?? 0,
          icon: <FiTarget />,
          colorClass: 'amber',
          link: '/students'
        },
        {
          label: t('dashboard.franceActiveCompanies'),
          value: fr.activeCompanies ?? 0,
          icon: <FiHome />,
          colorClass: 'green',
          link: '/companies'
        },
        {
          label: t('dashboard.franceOpenPositions'),
          value: fr.openPositions ?? 0,
          icon: <FiLayers />,
          colorClass: 'purple',
          link: '/jobs'
        },
        {
          label: t('dashboard.franceSignedContracts'),
          value: fr.signedContracts ?? 0,
          icon: <FiFileText />,
          colorClass: 'blue',
          link: '/agreements'
        }
      ]
    : [];

  const statCards = [
    {
      label: t('dashboard.statTotalB2bAgencies'),
      value: stats.totalB2BAgencies ?? 0,
      icon: <FiBriefcase />,
      colorClass: 'blue',
      link: '/b2b-agencies'
    },
    {
      label: t('dashboard.statTiedUpAgencies'),
      value: stats.tiedUpAgencies ?? 0,
      icon: <FiUserCheck />,
      colorClass: 'green',
      link: '/b2b-agencies?status=Tied-Up'
    },
    {
      label: t('dashboard.statActiveAgencies'),
      value: stats.activeAgenciesWithStudents ?? 0,
      icon: <FiActivity />,
      colorClass: 'purple',
      link: '/b2b-agencies'
    },
    {
      label: t('dashboard.statB2cStudents'),
      value: stats.totalB2CStudents ?? 0,
      icon: <FiUsers />,
      colorClass: 'teal',
      link: '/students?student_type=b2c'
    },
    {
      label: t('dashboard.statB2bStudents'),
      value: stats.totalB2BStudents ?? 0,
      icon: <FiUsers />,
      colorClass: 'amber',
      link: '/students?student_type=b2b'
    },
    {
      label: t('dashboard.statInterviewPassStudents'),
      value: stats.studentsInterviewPass ?? 0,
      icon: <FiCheckCircle />,
      colorClass: 'green',
      link: '/interviews'
    },
    {
      label: t('dashboard.statApplicationFeeReceived'),
      value: stats.studentsApplicationFeePaid ?? 0,
      icon: <FiDollarSign />,
      colorClass: 'blue',
      link: '/students'
    },
    {
      label: t('dashboard.statTuitionFeeReceived'),
      value: stats.studentsTuitionFeePaid ?? 0,
      icon: <FiCreditCard />,
      colorClass: 'purple',
      link: '/students'
    },
    ...(config.features.meetingRequired
      ? [{
          label: t('dashboard.meetingPendingOverdue'),
          value: overdueMeetingsCount || 0,
          icon: <FiCalendar />,
          colorClass: 'red',
          link: '/meetings?status=all_pending_overdue'
        }]
      : [])
  ];

  const selfIndiaRow = (indiaReports?.rows && indiaReports.rows[0]) || null;
  const indiaReportUserOptions = useMemo(
    () => (indiaReports?.rows || []).map((r) => ({ value: String(r.user_id), label: `${r.name} (${r.role})` })),
    [indiaReports?.rows]
  );
  const filteredIndiaReportRows = useMemo(() => {
    const allowed = new Set((selectedIndiaReportUsers || []).map((v) => String(v)));
    const rows = indiaReports?.rows || [];
    if (allowed.size === 0) return [];
    return rows.filter((r) => allowed.has(String(r.user_id)));
  }, [indiaReports?.rows, selectedIndiaReportUsers]);
  const indiaAgencyOptions = useMemo(
    () => (indiaB2bAgencyReport?.rows || []).map((r) => ({ value: String(r.lead_id), label: `${r.b2b_name}${r.ref_number ? ` (${r.ref_number})` : ''}` })),
    [indiaB2bAgencyReport?.rows]
  );
  const filteredIndiaAgencyRows = useMemo(() => {
    const allowed = new Set((selectedIndiaReportAgencies || []).map((v) => String(v)));
    const rows = indiaB2bAgencyReport?.rows || [];
    if (allowed.size === 0) return [];
    return rows.filter((r) => allowed.has(String(r.lead_id)));
  }, [indiaB2bAgencyReport?.rows, selectedIndiaReportAgencies]);

  useEffect(() => {
    const all = indiaReportUserOptions.map((o) => o.value);
    setSelectedIndiaReportUsers((prev) => {
      const next = (prev || []).filter((v) => all.includes(String(v)));
      return next.length ? next : all;
    });
  }, [indiaReportUserOptions]);

  useEffect(() => {
    const all = indiaAgencyOptions.map((o) => o.value);
    setSelectedIndiaReportAgencies((prev) => {
      const next = (prev || []).filter((v) => all.includes(String(v)));
      return next.length ? next : all;
    });
  }, [indiaAgencyOptions]);

  const renderIndiaDateRangeInputs = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 10 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
        <span style={{ color: 'var(--text-muted)' }}>{t('dashboard.indiaReportDateFrom')}</span>
        <input
          type="date"
          className="form-control"
          style={{ minWidth: 136, padding: '6px 8px', fontSize: 13 }}
          value={indiaDateFrom}
          onChange={(e) => setIndiaDateFrom(e.target.value)}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
        <span style={{ color: 'var(--text-muted)' }}>{t('dashboard.indiaReportDateTo')}</span>
        <input
          type="date"
          className="form-control"
          style={{ minWidth: 136, padding: '6px 8px', fontSize: 13 }}
          value={indiaDateTo}
          onChange={(e) => setIndiaDateTo(e.target.value)}
        />
      </label>
      <button
        type="button"
        className="btn btn-outline btn-sm"
        onClick={() => {
          const d = indiaTodayIsoKolkata();
          setIndiaDateFrom(d);
          setIndiaDateTo(d);
        }}
      >
        {t('dashboard.indiaReportDateToday')}
      </button>
    </div>
  );

  const indiaUserStatCards = selfIndiaRow
    ? [
        { label: t('dashboard.cardActivitiesTotal'), value: fmtIn(selfIndiaRow.activities_total), icon: <FiActivity />, colorClass: 'teal' },
        { label: t('dashboard.cardActivitiesFu'), value: fmtIn(selfIndiaRow.activities_follow_up), icon: <FiPhone />, colorClass: 'blue' },
        { label: t('dashboard.cardActivitiesMtg'), value: fmtIn(selfIndiaRow.activities_meeting), icon: <FiCalendar />, colorClass: 'purple' },
        { label: t('dashboard.cardActivitiesCall'), value: fmtIn(selfIndiaRow.activities_call), icon: <FiPhone />, colorClass: 'amber' },
        { label: t('dashboard.cardMeetSch'), value: fmtIn(selfIndiaRow.meetings_scheduled), icon: <FiCalendar />, colorClass: 'blue' },
        { label: t('dashboard.cardMeetResch'), value: fmtIn(selfIndiaRow.meetings_rescheduled), icon: <FiCalendar />, colorClass: 'purple' },
        { label: t('dashboard.cardMeetDone'), value: fmtIn(selfIndiaRow.meetings_completed), icon: <FiCheckCircle />, colorClass: 'green' },
        { label: t('dashboard.cardAgrGen'), value: fmtIn(selfIndiaRow.agreements_generated_agencies), icon: <FiFileText />, colorClass: 'blue' },
        { label: t('dashboard.cardAgrUp'), value: fmtIn(selfIndiaRow.agreements_uploaded_agencies), icon: <FiFileText />, colorClass: 'teal' },
        { label: t('dashboard.cardAgrVer'), value: fmtIn(selfIndiaRow.agreements_verified), icon: <FiCheckCircle />, colorClass: 'green' },
        { label: t('dashboard.cardAgrUnver'), value: fmtIn(selfIndiaRow.agreements_unverified), icon: <FiTarget />, colorClass: 'amber' },
        { label: t('dashboard.cardStudents'), value: fmtIn(selfIndiaRow.student_count), icon: <FiUsers />, colorClass: 'teal' },
        { label: t('dashboard.cardAppFeeCnt'), value: fmtIn(selfIndiaRow.application_fee_records), icon: <FiDollarSign />, colorClass: 'blue' },
        { label: t('dashboard.cardAppFeeSum'), value: fmtIn(selfIndiaRow.application_fee_total), icon: <FiDollarSign />, colorClass: 'green' },
        { label: t('dashboard.cardTuiCnt'), value: fmtIn(selfIndiaRow.tuition_fee_records), icon: <FiCreditCard />, colorClass: 'purple' },
        { label: t('dashboard.cardTuiSum'), value: fmtIn(selfIndiaRow.tuition_fee_total), icon: <FiCreditCard />, colorClass: 'green' },
        { label: t('dashboard.cardVisa'), value: fmtIn(selfIndiaRow.visa_received_count), icon: <FiUserCheck />, colorClass: 'green' }
      ]
    : [];

  if (loading) return <div className="loading-spinner"><div className="spinner"></div></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>
            {user?.name?.split(' ')[0]
              ? `${t('dashboard.welcome')} ${user.name.split(' ')[0]}!`
              : `${t('dashboard.welcome')} ${t('dashboard.userFallback')}`}
          </h2>
          <p className="page-subtitle">
            {new Date().toLocaleDateString(dateLocale, {
              timeZone: dateTimeZone,
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginBottom: 12 }}>{t('dashboard.quickActions')}</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => navigate('/b2b-agencies/new')}><FiPlus /> {t('dashboard.newAgency')}</button>
          <button className="btn btn-outline" onClick={() => navigate('/students/new')}><FiUserCheck /> {t('dashboard.newStudentDirect')}</button>
          {config.features.meetingRequired && (
            <button className="btn btn-outline" onClick={() => navigate('/meetings')}><FiCalendar /> {t('dashboard.scheduleMeeting')}</button>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/activities')}><FiPhone /> {t('dashboard.logActivity')}</button>
        </div>
      </div>

      {/* India: team report table (admin / super_admin / manager / sales_manager) */}
      {showIndiaTeamTable && (
        <div
          className="card"
          style={{
            marginBottom: 20,
            position: 'relative',
            pointerEvents: indiaWorkspaceLoading ? 'none' : 'auto',
            transition: 'none'
          }}
        >
          <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 12
              }}
            >
              <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t('dashboard.indiaReportsTitle')}</h3>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{t('dashboard.indiaReportsSubtitleTeam')}</p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
                {renderIndiaDateRangeInputs()}
                <MultiSelectDropdown
                  label="Users"
                  options={indiaReportUserOptions}
                  selectedValues={selectedIndiaReportUsers}
                  onChange={setSelectedIndiaReportUsers}
                  allText={t('common.all')}
                  noneText={t('common.none')}
                  selectAllText={t('common.selectAll')}
                  unselectAllText={t('common.unselectAll')}
                />
                {isAdmin ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                    onClick={() => {
                      try {
                        downloadIndiaTeamReportCsv(filteredIndiaReportRows || [], t, indiaReports?.dateFilter);
                        toast.success(t('dashboard.indiaReportsCsvDownloaded'));
                      } catch (e) {
                        console.error(e);
                        toast.error(t('dashboard.indiaReportsCsvFailed'));
                      }
                    }}
                  >
                    <FiDownload />
                    {t('dashboard.indiaReportsDownloadCsv')}
                  </button>
                ) : null}
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{t('dashboard.indiaReportDateFilterHelpShort')}</p>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table className="table" style={{ fontSize: 12, minWidth: 1200, whiteSpace: 'nowrap' }}>
                <thead>
                  <tr>
                    <th>{t('dashboard.colUser')}</th>
                    <th>{t('dashboard.colRole')}</th>
                    <th title={t('dashboard.colActivitiesTotal')}>{t('dashboard.colActivitiesTotal')}</th>
                    <th>{t('dashboard.colActivitiesFu')}</th>
                    <th>{t('dashboard.colActivitiesMtg')}</th>
                    <th>{t('dashboard.colActivitiesCall')}</th>
                    <th>{t('dashboard.colMeetSch')}</th>
                    <th>{t('dashboard.colMeetResch')}</th>
                    <th>{t('dashboard.colMeetDone')}</th>
                    <th>{t('dashboard.colAgrGen')}</th>
                    <th>{t('dashboard.colAgrUp')}</th>
                    <th>{t('dashboard.colAgrVer')}</th>
                    <th>{t('dashboard.colAgrUnver')}</th>
                    <th>{t('dashboard.colStudents')}</th>
                    <th>{t('dashboard.colAppFeeCnt')}</th>
                    <th>{t('dashboard.colAppFeeSum')}</th>
                    <th>{t('dashboard.colTuiCnt')}</th>
                    <th>{t('dashboard.colTuiSum')}</th>
                    <th>{t('dashboard.colVisa')}</th>
                    <th>{t('dashboard.colB2bAgencies')}</th>
                    <th>{t('dashboard.colB2bActive')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIndiaReportRows.map((r) => (
                    <tr key={r.user_id}>
                      <td style={{ fontWeight: 600 }}>{r.name}</td>
                      <td>{r.role}</td>
                      <td>{fmtIn(r.activities_total)}</td>
                      <td>{fmtIn(r.activities_follow_up)}</td>
                      <td>{fmtIn(r.activities_meeting)}</td>
                      <td>{fmtIn(r.activities_call)}</td>
                      <td>{fmtIn(r.meetings_scheduled)}</td>
                      <td>{fmtIn(r.meetings_rescheduled)}</td>
                      <td>{fmtIn(r.meetings_completed)}</td>
                      <td>{fmtIn(r.agreements_generated_agencies)}</td>
                      <td>{fmtIn(r.agreements_uploaded_agencies)}</td>
                      <td>{fmtIn(r.agreements_verified)}</td>
                      <td>{fmtIn(r.agreements_unverified)}</td>
                      <td>{fmtIn(r.student_count)}</td>
                      <td>{fmtIn(r.application_fee_records)}</td>
                      <td>{fmtIn(r.application_fee_total)}</td>
                      <td>{fmtIn(r.tuition_fee_records)}</td>
                      <td>{fmtIn(r.tuition_fee_total)}</td>
                      <td>{fmtIn(r.visa_received_count)}</td>
                      <td>{fmtIn(r.b2b_agencies_count)}</td>
                      <td>{fmtIn(r.b2b_active_agencies_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* India: self-service stat cards (non–team-viewer roles) */}
      {showIndiaUserCards && indiaUserStatCards.length > 0 && (
        <div
          style={{
            marginBottom: 20,
            pointerEvents: indiaWorkspaceLoading ? 'none' : 'auto',
            transition: 'none'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12
            }}
          >
            {renderIndiaDateRangeInputs()}
          </div>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{t('dashboard.indiaReportDateFilterHelpShort')}</p>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700 }}>{t('dashboard.indiaReportsTitle')}</h3>
          <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--text-muted)' }}>{t('dashboard.indiaReportsSubtitleSelf')}</p>
          {selfIndiaRow ? (
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>
              {t('dashboard.indiaReportsSelfB2bLine', {
                b2b: fmtIn(selfIndiaRow.b2b_agencies_count),
                active: fmtIn(selfIndiaRow.b2b_active_agencies_count)
              })}
            </p>
          ) : null}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
            {indiaUserStatCards.map((card, i) => (
              <div
                key={i}
                className="stat-card"
                onClick={() => card.link && navigate(card.link)}
                style={{ cursor: card.link ? 'pointer' : 'default', padding: '12px 14px' }}
              >
                <div className={`stat-icon ${card.colorClass}`} style={{ width: 40, height: 40, fontSize: 18 }}>{card.icon}</div>
                <div className="stat-info">
                  <div className="stat-value" style={{ fontSize: 20 }}>{card.value}</div>
                  <div className="stat-label" style={{ fontSize: 11, lineHeight: 1.25 }}>{card.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* India: B2B agency report (per agency — all agencies for super_admin / admin / manager; others scoped) */}
      {showIndiaB2bAgencyTable && (
        <div
          className="card"
          style={{
            marginBottom: 20,
            position: 'relative',
            pointerEvents: indiaWorkspaceLoading ? 'none' : 'auto',
            transition: 'none'
          }}
        >
          <div className="card-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 12
              }}
            >
              <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h3 style={{ margin: 0 }}>{t('dashboard.indiaB2bAgencyReportTitle')}</h3>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{t(indiaB2bAgencySubtitleKey)}</p>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
                {renderIndiaDateRangeInputs()}
                <MultiSelectDropdown
                  label={t('dashboard.colB2bAgencyName')}
                  options={indiaAgencyOptions}
                  selectedValues={selectedIndiaReportAgencies}
                  onChange={setSelectedIndiaReportAgencies}
                  allText={t('common.all')}
                  noneText={t('common.none')}
                  selectAllText={t('common.selectAll')}
                  unselectAllText={t('common.unselectAll')}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
                  disabled={!filteredIndiaAgencyRows.length}
                  onClick={() => {
                    try {
                      downloadIndiaB2bAgencyReportCsv(filteredIndiaAgencyRows || [], t, indiaB2bAgencyReport?.dateFilter);
                      toast.success(t('dashboard.indiaReportsCsvDownloaded'));
                    } catch (e) {
                      console.error(e);
                      toast.error(t('dashboard.indiaReportsCsvFailed'));
                    }
                  }}
                >
                  <FiDownload />
                  {t('dashboard.indiaReportsDownloadCsv')}
                </button>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{t('dashboard.indiaReportDateFilterHelpShort')}</p>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            {filteredIndiaAgencyRows.length === 0 ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>{t('dashboard.indiaB2bAgencyReportEmpty')}</p>
            ) : (
              <div className="table-responsive" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="table" style={{ fontSize: 12, minWidth: 720, whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr>
                      <th>{t('dashboard.colB2bAgencyName')}</th>
                      <th>{t('dashboard.colRefNumber')}</th>
                      <th>{t('dashboard.colStudents')}</th>
                      <th>{t('dashboard.colAppFeeCnt')}</th>
                      <th>{t('dashboard.colAppFeeSum')}</th>
                      <th>{t('dashboard.colTuiCnt')}</th>
                      <th>{t('dashboard.colTuiSum')}</th>
                      <th>{t('dashboard.colVisa')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredIndiaAgencyRows.map((r) => (
                      <tr
                        key={r.lead_id}
                        onClick={() => navigate(`/b2b-agencies/${r.lead_id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td style={{ fontWeight: 600 }}>{r.b2b_name}</td>
                        <td>{r.ref_number || '—'}</td>
                        <td>{fmtIn(r.student_count)}</td>
                        <td>{fmtIn(r.application_fee_records)}</td>
                        <td>{fmtIn(r.application_fee_total)}</td>
                        <td>{fmtIn(r.tuition_fee_records)}</td>
                        <td>{fmtIn(r.tuition_fee_total)}</td>
                        <td>{fmtIn(r.visa_received_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Default stat cards (hidden for India when team table or India user cards are shown) */}
      {!hideDefaultIndiaStatCards && (
      <div className="stats-grid">
        {statCards.map((card, i) => (
          <div key={i} className="stat-card" onClick={() => card.link && navigate(card.link)} style={{ cursor: card.link ? 'pointer' : 'default' }}>
            <div className={`stat-icon ${card.colorClass}`}>{card.icon}</div>
            <div className="stat-info">
              <div className="stat-value">{card.value}</div>
              <div className="stat-label">{card.label}</div>
            </div>
          </div>
        ))}
      </div>
      )}

      {isFranceCRM && franceOverviewCards.length > 0 && (
        <>
          <h3 style={{ margin: '24px 0 12px', fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)' }}>
            {t('dashboard.franceKeyMetricsTitle')}
          </h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)' }}>{t('dashboard.franceKeyMetricsSubtitle')}</p>
          <div className="stats-grid">
            {franceOverviewCards.map((card, i) => (
              <div
                key={`fr-${i}`}
                className="stat-card"
                onClick={() => card.link && navigate(card.link)}
                style={{ cursor: card.link ? 'pointer' : 'default' }}
              >
                <div className={`stat-icon ${card.colorClass}`}>{card.icon}</div>
                <div className="stat-info">
                  <div className="stat-value">{card.value}</div>
                  <div className="stat-label">{card.label}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Today's Birthdays */}
        <div className="card">
          <div className="card-header">
            <h3>{t('dashboard.todaysBirthdays')}</h3>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/students')}>
              {t('common.viewAll')} <FiArrowRight />
            </button>
          </div>
          <div className="card-body">
            {birthdaysToday.length > 0 ? birthdaysToday.map((s) => {
              const studentName = [s.first_name, s.last_name].filter(Boolean).join(' ') || s.student_ref || `#${s.student_id}`;
              const age = computeAge(s.dob);
              return (
                <div
                  key={s.student_id}
                  className="list-item"
                  onClick={() => navigate(`/students/${s.student_id}`)}
                  style={{ cursor: 'pointer' }}
                  title={t('dashboard.openStudentDetails')}
                >
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{studentName}</strong>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {s.student_ref || `#${s.student_id}`}{age != null ? ` • ${t('dashboard.turningAge', { age })}` : ''}
                    </div>
                  </div>
                  <FiCalendar size={14} />
                </div>
              );
            }) : <div className="empty-state"><p>{t('dashboard.noBirthdaysToday')}</p></div>}
          </div>
        </div>

        {/* Recent Leads */}
        <div className="card">
          <div className="card-header">
            <h3>{t('dashboard.recentAgencies')}</h3>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/b2b-agencies')}>{t('common.viewAll')} <FiArrowRight /></button>
          </div>
          <div className="card-body">
            {recentLeads.length > 0 ? recentLeads.map(lead => (
              <div key={lead.lead_id} className="list-item" onClick={() => navigate(`/b2b-agencies/${lead.lead_id}`)} style={{ cursor: 'pointer' }}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.student_name}</strong>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{lead.ref_number} &bull; {lead.source || t('common.na')}</div>
                </div>
                <StatusBadge status={lead.status} />
              </div>
            )) : <div className="empty-state"><p>{t('dashboard.noRecentAgencies')}</p></div>}
          </div>
        </div>

        {config.features.meetingRequired && (
          <>
            {/* Upcoming Meetings */}
            <div className="card">
              <div className="card-header">
                <h3>{t('dashboard.upcomingMeetings')}</h3>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/meetings')}>{t('common.viewAll')} <FiArrowRight /></button>
              </div>
              <div className="card-body">
                {upcomingMeetings.length > 0 ? upcomingMeetings.map(m => (
                  <div key={m.meeting_id} className="list-item">
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</strong>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiCalendar size={11} /> {new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} at {m.meeting_time}
                        {m.location && ` • ${m.location}`}
                      </div>
                    </div>
                    <StatusBadge status={m.meeting_type} />
                  </div>
                )) : <div className="empty-state"><p>{t('dashboard.noUpcomingMeetings')}</p></div>}
              </div>
            </div>

            {/* Overdue Meetings */}
            <div className="card">
              <div className="card-header">
                <h3>{t('dashboard.overdueMeetings')}</h3>
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/meetings?status=all_pending_overdue')}>{t('common.viewAll')} <FiArrowRight /></button>
              </div>
              <div className="card-body">
                {overdueMeetings.length > 0 ? overdueMeetings.map(m => (
                  <div
                    key={m.meeting_id}
                    className="list-item"
                    style={{ borderLeft: '3px  solid var(--danger)', paddingLeft: 12, cursor: 'pointer' , marginBottom: 8 }}
                    onClick={() => navigate('/meetings?status=all_pending_overdue')}
                    title={t('dashboard.openOverdueMeetings')}
                  >
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</strong>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <FiCalendar size={11} /> {new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })} at {m.meeting_time}
                      </div>
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700, flexShrink: 0 }}>{t('common.overdue')}</span>
                  </div>
                )) : <div className="empty-state"><p>{t('dashboard.noOverdueMeetings')}</p></div>}
              </div>
            </div>
          </>
        )}

        {/* Pending Follow-ups */}
        <div className="card">
          <div className="card-header">
            <h3>{t('dashboard.pendingFollowups')}</h3>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/activities')}>{t('common.viewAll')} <FiArrowRight /></button>
          </div>
          <div className="card-body">
            {followups.length > 0 ? followups.slice(0, 6).map((f) => {
              const isOverdue = isFollowupOverdueDate(f.next_followup);
              const targetPath = f.prospect_id
                ? `/prospects/${f.prospect_id}`
                : f.lead_id
                  ? `/b2b-agencies/${f.lead_id}`
                  : (f.student_id ? `/students/${f.student_id}` : '/activities');
              const entityLabel = f.prospect_id
                ? t('dashboard.prospect')
                : f.lead_id
                  ? t('dashboard.lead')
                  : f.student_id
                    ? t('dashboard.student')
                    : '';
              const name = f.prospect_name || f.lead_name || f.student_full_name || '—';
              const titleLine = entityLabel
                ? `${f.activity_type} ${entityLabel}: ${name}`
                : `${f.activity_type}: ${name}`;
              const activityWhen = f.activity_date
                ? `${new Date(f.activity_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}${f.activity_time ? ` ${String(f.activity_time).slice(0, 8)}` : ''}`
                : '';
              const dueStr = f.next_followup
                ? new Date(String(f.next_followup).slice(0, 10)).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
                : '';
              const notesPreview = f.notes ? String(f.notes).replace(/\s+/g, ' ').trim().slice(0, 80) : '';
              return (
                <div
                  key={`${f.source_table || 'activity'}:${f.item_id || `${f.lead_id || ''}-${f.student_id || ''}-${f.next_followup || ''}`}`}
                  className="list-item"
                  style={{
                    borderLeft: `3px solid ${isOverdue ? 'var(--danger)' : 'var(--warning)'}`,
                    paddingLeft: 12,
                    alignItems: 'flex-start',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 8,
                    gap: 12
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    style={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
                    onClick={() => navigate(targetPath)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(targetPath);
                      }
                    }}
                    title={t('dashboard.openFollowupDetails')}
                  >
                    <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleLine}</strong>
                    {activityWhen ? (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{activityWhen}</div>
                    ) : null}
                    {notesPreview ? (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{notesPreview}{f.notes && String(f.notes).length > 80 ? '…' : ''}</div>
                    ) : null}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {f.created_by_name ? <>{t('dashboard.byPrefix')} {f.created_by_name}</> : null}
                      {f.created_by_name && dueStr ? ' · ' : null}
                      {dueStr ? <>{t('dashboard.nextFollowup')} {dueStr}</> : null}
                    </div>
                  </div>
                  <div
                    style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {isOverdue ? (
                      <span style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 700 }}>{t('common.overdue')}</span>
                    ) : null}
                    {f.item_id != null ? (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        style={{ whiteSpace: 'nowrap' }}
                        disabled={completingFollowupId === f.item_id}
                        onClick={(e) => handleCompleteFollowup(f, e)}
                      >
                        <FiCheckCircle size={14} /> {completingFollowupId === f.item_id ? t('common.saving') : t('dashboard.markFollowupCompleted')}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            }) : <div className="empty-state"><p>{t('dashboard.noPendingFollowups')}</p></div>}
          </div>
        </div>

        {/* Lead Status Distribution */}
        <div className="card">
          <div className="card-header">
            <h3>{t('dashboard.leadPipeline')}</h3>
          </div>
          <div className="card-body">
            {conversionData.length > 0 ? (
              <div>
                {conversionData.map((item, i) => {
                  const maxCount = Math.max(...conversionData.map(d => d.count || 0));
                  const percent = maxCount > 0 ? ((item.count || 0) / maxCount) * 100 : 0;
                  return (
                    <div key={i} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>{item.status}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{item.count}</span>
                      </div>
                      <div style={{ height: 8, background: 'var(--bg-secondary)', borderRadius: 4 }}>
                        <div style={{ height: '100%', width: `${percent}%`, background: 'var(--primary)', borderRadius: 4, transition: 'width 0.5s ease' }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div className="empty-state"><p>{t('dashboard.noPipelineData')}</p></div>}
          </div>
        </div>
      </div>

      {/* Refund Dashboard Widgets (Admin/Manager only) */}
      {isAdminOrManager && refundStats && (
        <div style={{ marginTop: 20 }}>
          <h3 style={{ marginBottom: 12 }}>Refund Requests Overview</h3>
          <div className="stats-grid">
            <div className="stat-card" style={{ cursor: 'pointer', borderLeft: '4px solid #ffc107' }} onClick={() => navigate('/refund-requests?status=pending_approval')}>
              <div className="stat-icon" style={{ background: '#fff3cd', color: '#856404' }}>⏳</div>
              <div className="stat-info">
                <h3>{refundStats.total_pending || 0}</h3>
                <p>Pending Approval</p>
              </div>
            </div>
            <div className="stat-card" style={{ cursor: 'pointer', borderLeft: '4px solid var(--primary)' }} onClick={() => navigate('/refund-requests?read_status=unread')}>
              <div className="stat-icon" style={{ background: 'var(--primary-50)', color: 'var(--primary)' }}>📬</div>
              <div className="stat-info">
                <h3>{refundStats.unread_requests || 0}</h3>
                <p>Unread Requests</p>
              </div>
            </div>
            <div className="stat-card" style={{ cursor: 'pointer', borderLeft: '4px solid #dc3545' }} onClick={() => navigate('/refund-requests?priority=high')}>
              <div className="stat-icon" style={{ background: '#f8d7da', color: '#dc3545' }}>🔴</div>
              <div className="stat-info">
                <h3>{refundStats.high_priority || 0}</h3>
                <p>High Priority</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #28a745' }}>
              <div className="stat-icon" style={{ background: '#d4edda', color: '#28a745' }}>✅</div>
              <div className="stat-info">
                <h3>{refundStats.approved_today || 0}</h3>
                <p>Approved Today</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #dc3545' }}>
              <div className="stat-icon" style={{ background: '#f8d7da', color: '#dc3545' }}>❌</div>
              <div className="stat-info">
                <h3>{refundStats.rejected_today || 0}</h3>
                <p>Rejected Today</p>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #17a2b8' }}>
              <div className="stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}>📋</div>
              <div className="stat-info">
                <h3>{refundStats.processed_this_week || 0}</h3>
                <p>Processed This Week</p>
              </div>
            </div>
          </div>
          {refundStats.amount_summary && Object.keys(refundStats.amount_summary).length > 0 && (
            <div className="card" style={{ marginTop: 12, padding: 16 }}>
              <strong style={{ fontSize: 13 }}>Approved/Processed Refund Totals:</strong>
              <div style={{ display: 'flex', gap: 20, marginTop: 8 }}>
                {Object.entries(refundStats.amount_summary).map(([curr, amt]) => (
                  <div key={curr}>
                    <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{curr} {Number(amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {refundStats.recent_requests?.length > 0 && (
            <div className="card" style={{ marginTop: 12, padding: 0, overflow: 'hidden' }}>
              <h4 style={{ padding: '12px 16px 0', fontWeight: 700 }}>Recent Refund Requests</h4>
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Student</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refundStats.recent_requests.map((r) => (
                      <tr key={r.refund_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/refund-requests/${r.refund_id}`)}>
                        <td style={{ fontWeight: 600 }}>#{r.refund_id}</td>
                        <td>{r.student_ref} — {r.first_name} {r.last_name}</td>
                        <td>{String(r.refund_type || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</td>
                        <td style={{ fontWeight: 600 }}>{r.currency} {parseFloat(r.amount_refunded || 0).toFixed(2)}</td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                            background: { pending_approval: '#fef3cd', approved: '#d4edda', rejected: '#f8d7da' }[r.status] || '#e2e3e5',
                            color: { pending_approval: '#856404', approved: '#155724', rejected: '#721c24' }[r.status] || '#383d41'
                          }}>
                            {(r.status || '').replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600, color: { high: '#dc3545', medium: '#fd7e14', low: '#6c757d' }[r.priority] || '#6c757d' }}>
                          {(r.priority || '').toUpperCase()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
